import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { put } from "@/lib/api";
import type { ClientOption } from "@/lib/clients";
import { evaluate, linesFromRule, type ShareLine } from "@/lib/shareLines";
import type { ExpenseRuleRow } from "@/lib/types";
import { ShareLinesEditor } from "./ShareLinesEditor";

/** Edits a rule's share lines (incl. a standing Rechnungs-Nr. per line) and its note. */
export function RuleDialog({ rule, clients, defaultMarginPct, onClose }: {
  rule: ExpenseRuleRow | null;
  clients: ClientOption[];
  defaultMarginPct: number;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  // The last opened rule stays rendered while the dialog animates out (rule is already null then).
  const [shown, setShown] = useState<ExpenseRuleRow | null>(null);
  const [lines, setLines] = useState<ShareLine[]>([]);
  const [note, setNote] = useState("");
  useEffect(() => {
    if (!rule) return;
    setShown(rule);
    setLines(linesFromRule(rule));
    setNote(rule.note ?? "");
  }, [rule]);

  const customer = shown?.classification === "customer";
  const ev = evaluate(lines, null, defaultMarginPct);
  const save = useMutation({
    mutationFn: () => put<{ autoClassified: number }>(`/api/expense-rules/${shown!.id}`, { note: note.trim() || null, ...(customer ? { shares: ev.specs } : {}) }),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ["rules"] });
      qc.invalidateQueries({ queryKey: ["expenses"] });
      qc.invalidateQueries({ queryKey: ["unbilled"] });
      toast.success(res.autoClassified > 0 ? `Regel gespeichert · ${res.autoClassified} offene zugeordnet` : "Regel gespeichert");
      onClose();
    },
    onError: (e) => toast.error(e.message),
  });
  const invalid = customer && ev.error !== null;

  return (
    <Dialog open={rule !== null} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader><DialogTitle>Regel bearbeiten</DialogTitle></DialogHeader>
        <form className="contents" onSubmit={(e) => { e.preventDefault(); if (!save.isPending && !invalid) save.mutate(); }}>
          <p className="text-sm text-muted-foreground">{shown?.payee_pattern}{customer ? "" : " · Allgemein"}</p>
          {customer && (
            <div className="space-y-2">
              <ShareLinesEditor lines={lines} onChange={setLines} clients={clients} defaultMarginPct={defaultMarginPct} refPlaceholder="offen" />
              <p className={ev.error ? "text-sm text-destructive" : "text-sm text-muted-foreground"}>
                {ev.error ?? "Eine Rechnungs-Nr. wird auf jeden neuen Anteil dieser Zeile gesetzt, etwa eine Pauschal-Rechnung oder „nicht_fakturieren_2026“ für nur intern. Leer: der Anteil wartet unter Verrechnung. Bereits zugeordnete Ausgaben bleiben unverändert."}
              </p>
            </div>
          )}
          <div className="space-y-1.5"><Label htmlFor="rule-note">Notiz (optional)</Label><Input id="rule-note" value={note} onChange={(e) => setNote(e.target.value)} /></div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>Abbrechen</Button>
            <Button disabled={save.isPending || invalid} type="submit">Speichern</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
